import React, { useState, useEffect, useMemo } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/common/Primitives";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, DollarSign, Percent, TrendingUp, PiggyBank, Gauge, Target } from "lucide-react";

export default function ROI() {
  const [data, setData] = useState(null);
  const [cost, setCost] = useState(35000);
  const [revenue, setRevenue] = useState(600000);
  const [margin, setMargin] = useState(25);

  useEffect(() => {
    api.get("/roi").then(({ data }) => {
      setData(data);
      setCost(data.platform_cost || 35000);
      setRevenue(data.incremental_revenue || 600000);
      setMargin(data.gross_margin || 25);
    }).catch(() => {});
  }, []);

  const calc = useMemo(() => {
    const gp = revenue * (margin / 100);
    const net = gp - cost;
    const roi = cost ? gp / cost : 0;
    const breakEven = margin ? cost / (margin / 100) : 0;
    return { gp, net, roi, breakEven };
  }, [cost, revenue, margin]);

  if (!data) return <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  const breakEvenExamples = [20000, 35000, 50000].map((c) => ({ cost: c, req: c / (margin / 100) }));

  return (
    <div>
      <PageHeader title="ROI & Break-even" subtitle="Prove the return on every rupee invested in the platform" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="roi-platform-cost" label="Platform Investment" value={data.platform_cost} currency icon={Wallet} accent="slate" />
        <KpiCard testid="roi-inc-revenue" label="Incremental Export Revenue" value={data.incremental_revenue} currency icon={DollarSign} accent="sky" />
        <KpiCard testid="roi-margin" label="Gross Margin" value={`${data.gross_margin}%`} icon={Percent} accent="amber" />
        <KpiCard testid="roi-inc-gp" label="Incremental Gross Profit" value={data.incremental_gross_profit} currency icon={TrendingUp} accent="emerald" />
        <KpiCard testid="roi-net" label="Net Incremental Profit" value={data.net_incremental_profit} currency icon={PiggyBank} accent={data.net_incremental_profit >= 0 ? "emerald" : "rose"} />
        <KpiCard testid="roi-multiple" label="ROI Multiple" value={`${data.roi}×`} icon={Gauge} accent="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 border border-slate-200/80 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 font-display mb-1">ROI Calculator</h3>
          <p className="text-sm text-slate-500 mb-6">Model your return using your own assumptions. Attribution is user-identified — the platform never auto-claims credit for an order.</p>
          <div className="space-y-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Platform cost (₹/mo)</Label><Input type="number" value={cost} onChange={(e) => setCost(Number(e.target.value))} data-testid="roi-input-cost" className="bg-white" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Incremental revenue (₹)</Label><Input type="number" value={revenue} onChange={(e) => setRevenue(Number(e.target.value))} data-testid="roi-input-revenue" className="bg-white" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Gross margin (%)</Label><Input type="number" value={margin} onChange={(e) => setMargin(Number(e.target.value))} data-testid="roi-input-margin" className="bg-white" /></div>
            </div>
            <div>
              <Label className="text-xs">Gross margin: {margin}%</Label>
              <Slider value={[margin]} onValueChange={(v) => setMargin(v[0])} min={5} max={60} step={1} className="mt-3" data-testid="roi-margin-slider" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Gross Profit</p>
                <p className="text-2xl font-bold font-display text-emerald-700 mt-1" data-testid="roi-calc-gp">{fmtCurrency(calc.gp)}</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-900 text-white">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">ROI Multiple</p>
                <p className="text-2xl font-bold font-display mt-1" data-testid="roi-calc-multiple">{calc.roi.toFixed(2)}×</p>
              </div>
              <div className="p-4 rounded-xl bg-sky-50 border border-sky-100">
                <p className="text-xs uppercase tracking-wider font-semibold text-sky-700">Net Incremental Profit</p>
                <p className="text-2xl font-bold font-display text-sky-700 mt-1" data-testid="roi-calc-net">{fmtCurrency(calc.net)}</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-xs uppercase tracking-wider font-semibold text-amber-700">Break-even Revenue</p>
                <p className="text-2xl font-bold font-display text-amber-700 mt-1" data-testid="roi-calc-breakeven">{fmtCurrency(calc.breakEven)}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="lg:col-span-5 border border-slate-200/80 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 font-display mb-1 flex items-center gap-2"><Target className="h-5 w-5 text-slate-400" />Break-even Calculator</h3>
          <p className="text-sm text-slate-500 mb-5">Your platform investment is recovered when incremental gross profit reaches your monthly subscription cost.</p>
          <div className="space-y-2">
            {breakEvenExamples.map((b) => (
              <div key={b.cost} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/60">
                <span className="text-sm text-slate-600">{fmtCurrency(b.cost)} subscription @ {margin}% margin</span>
                <span className="text-sm font-bold text-slate-900">{fmtCurrency(b.req)} sales needed</span>
              </div>
            ))}
          </div>
          <div className="mt-5 p-4 rounded-xl bg-slate-900 text-white">
            <p className="text-xs text-slate-400">At your current plan ({fmtCurrency(cost)}/mo)</p>
            <p className="text-sm mt-1">You break even at <span className="font-bold text-sky-400">{fmtCurrency(calc.breakEven)}</span> of incremental export sales.</p>
          </div>
        </Card>
      </div>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6 mt-6">
        <h3 className="text-lg font-semibold text-slate-800 font-display mb-4">Monthly ROI Trend</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => fmtCurrency(v, false)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="incremental_revenue" name="Inc. Revenue" stroke="#0284c7" strokeWidth={2.5} />
              <Line yAxisId="left" type="monotone" dataKey="gross_profit" name="Gross Profit" stroke="#059669" strokeWidth={2.5} />
              <Line yAxisId="right" type="monotone" dataKey="roi" name="ROI (×)" stroke="#d97706" strokeWidth={2.5} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto mt-4">
          <Table>
            <TableHeader><TableRow className="bg-slate-50/80">{["Month", "Platform Cost", "Inc. Revenue", "Gross Profit", "ROI"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.trend.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium text-slate-800">{t.month}</TableCell>
                  <TableCell>{fmtCurrency(t.platform_cost)}</TableCell>
                  <TableCell>{fmtCurrency(t.incremental_revenue)}</TableCell>
                  <TableCell className="text-emerald-600 font-medium">{fmtCurrency(t.gross_profit)}</TableCell>
                  <TableCell className="font-bold">{t.roi}×</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
